import type { ReactNode } from 'react'
import type { IntlShape } from 'react-intl'

import BodyText from 'LuxKit/Typography/BodyText'
import Modal from 'LuxKit/Modal/Modal'
import HelpPane from 'components/Common/HelpPane'
import { isRecord } from 'lib/object/objectUtils'
import {
  ALL_FLEXIBLE_CANCELLATION_POLICIES,
  getFreeCancellationStartDate,
  isPostPurchaseSevenDayCancellationActive,
} from 'lib/offer/cancellationPolicyUtils'
import OfferPropertyCancellationPolicyModal from 'components/Common/CancellationPolicies/Flash/OfferPropertyCancellationPolicyModal'
import OfferPropertyFlexibleCancellationPolicyTimelineModal from 'components/Common/CancellationPolicies/Flash/OfferPropertyFlexibleCancellationPolicyTimelineModal'
import { getFlexibleCancellationTimelineDetails } from 'components/Common/CancellationPolicies/Flash/flexibleCancellationPolicyTimelineUtils'
import BedbankCancellationPolicyModal from 'components/OfferPage/Bedbank/BedbankCancellationPolicyModal'

function getFirstString(values: unknown): string | undefined {
  if (!Array.isArray(values)) return undefined
  return values.find((value): value is string => typeof value === 'string' && value.length > 0)
}

export function getCancellationText(
  cancellationPolicy: unknown,
  intl: IntlShape,
  checkIn?: string,
  purchaseDate?: string,
): string | undefined {
  if (typeof cancellationPolicy === 'string') {
    if (cancellationPolicy === 'non_refundable' || cancellationPolicy === 'non-refundable') {
      return intl.formatMessage({
        id: 'KiEo_I7vbSQkSAByaxbq',
        defaultMessage: 'Non-refundable',
        description: 'Non-refundable cancellation label in trip planner chat hotel room options',
      })
    }
    if (cancellationPolicy.toLowerCase().includes('free')) {
      return intl.formatMessage({
        id: 'pn5flUt3sj-BuwFiowHi',
        defaultMessage: 'Free cancellation',
        description: 'Free cancellation label in trip planner chat hotel room options',
      })
    }
    return cancellationPolicy
  }

  if (!isRecord(cancellationPolicy)) return undefined

  const type = typeof cancellationPolicy.type === 'string' ? cancellationPolicy.type : undefined
  const policyDetail = getFirstString(cancellationPolicy.policyDetail)
  const description = getFirstString(cancellationPolicy.description)

  if (type === 'non-refundable') {
    return intl.formatMessage({
      id: 'KiEo_I7vbSQkSAByaxbq',
      defaultMessage: 'Non-refundable',
      description: 'Non-refundable cancellation label in trip planner chat hotel room options',
    })
  }

  if (type === 'post-purchase-seven-days' && isPostPurchaseSevenDayCancellationActive(checkIn, purchaseDate)) {
    return intl.formatMessage({
      id: 'xojpLmyWhFodzJ17AaiZ',
      defaultMessage: 'Free cancellation within 7 days of purchase',
      description: 'Seven-day free cancellation label in trip planner chat hotel room options',
    })
  }

  if (type?.includes('flexible') || type?.includes('prior-to-check-in')) {
    return intl.formatMessage({
      id: 'SZRYTKB4IEcGmwDh2dFZ',
      defaultMessage: 'Flexible cancellation',
      description: 'Flexible cancellation label in trip planner chat hotel room options',
    })
  }

  if (cancellationPolicy.refundable === true) {
    return intl.formatMessage({
      id: 'pn5flUt3sj-BuwFiowHi',
      defaultMessage: 'Free cancellation',
      description: 'Free cancellation label in trip planner chat hotel room options',
    })
  }

  if (cancellationPolicy.partiallyRefundable === true) {
    return intl.formatMessage({
      id: 'Rzxibkya9ay3IeeOhO7-',
      defaultMessage: 'Partially refundable',
      description: 'Partially refundable cancellation label in trip planner chat hotel room options',
    })
  }

  return policyDetail ?? description
}

/**
 * The subset of a rate this module needs, kept structural so the cancellation helpers
 * stay importable from the rate row without a circular type import.
 */
interface CancellableRate {
  cancellationPolicy?: unknown
  refundableStatus?: 'refundable' | 'partially' | 'non-refundable'
}

/**
 * Cancellation label for a rate.
 *
 * The policy object is the richer source and wins where we can describe it. A live rate
 * can arrive with an empty or unrecognised policy while still declaring
 * `refundableStatus`, though, and the offer page still tags those - so fall back to it
 * rather than rendering a rate with no cancellation tag at all.
 */
export function getRateCancellationLabel(
  rate: CancellableRate,
  intl: IntlShape,
  checkIn?: string,
  purchaseDate?: string,
): string | undefined {
  const fromPolicy = getCancellationText(rate.cancellationPolicy, intl, checkIn, purchaseDate)
  if (fromPolicy) return fromPolicy

  switch (rate.refundableStatus) {
    case 'non-refundable':
      return intl.formatMessage({
        id: 'KiEo_I7vbSQkSAByaxbq',
        defaultMessage: 'Non-refundable',
        description: 'Non-refundable cancellation label in trip planner chat hotel room options',
      })
    case 'refundable':
      return intl.formatMessage({
        id: 'pn5flUt3sj-BuwFiowHi',
        defaultMessage: 'Free cancellation',
        description: 'Free cancellation label in trip planner chat hotel room options',
      })
    case 'partially':
      return intl.formatMessage({
        id: 'Rzxibkya9ay3IeeOhO7-',
        defaultMessage: 'Partially refundable',
        description: 'Partially refundable cancellation label in trip planner chat hotel room options',
      })
    default:
      return undefined
  }
}

/** Whether a rate reads as non-refundable, preferring the explicit status when present. */
export function isRateNonRefundable(rate: CancellableRate): boolean {
  if (rate.refundableStatus) return rate.refundableStatus === 'non-refundable'
  return isNonRefundableCancellationPolicy(rate.cancellationPolicy)
}

function getStringArray(values: unknown): Array<string> {
  if (!Array.isArray(values)) return []
  return values.filter((value): value is string => typeof value === 'string' && value.length > 0)
}

export function isNonRefundableCancellationPolicy(cancellationPolicy: unknown): boolean {
  if (typeof cancellationPolicy === 'string') {
    return cancellationPolicy === 'non_refundable' || cancellationPolicy === 'non-refundable'
  }

  if (!isRecord(cancellationPolicy)) return false

  return cancellationPolicy.type === 'non-refundable' ||
    (
      cancellationPolicy.refundable === false &&
      cancellationPolicy.partiallyRefundable !== true
    )
}

function isOfferCancellationPolicy(cancellationPolicy: unknown): cancellationPolicy is App.OfferCancellationPolicy {
  return (
    isRecord(cancellationPolicy) &&
    typeof cancellationPolicy.type === 'string' &&
    !Array.isArray(cancellationPolicy.policies)
  )
}

function isBedbankCancellationPolicy(cancellationPolicy: unknown): cancellationPolicy is App.BedbankCancellationPolicy {
  return (
    isRecord(cancellationPolicy) &&
    Array.isArray(cancellationPolicy.policies) &&
    Array.isArray(cancellationPolicy.description)
  )
}

function isFlexibleOfferCancellationPolicy(cancellationPolicy: App.OfferCancellationPolicy): boolean {
  return !!cancellationPolicy.type && ALL_FLEXIBLE_CANCELLATION_POLICIES.includes(cancellationPolicy.type)
}

function GenericCancellationPolicyModal({
  title,
  descriptions,
}: {
  title: string
  descriptions: Array<string>
}) {
  return (
    <Modal title={title}>
      {descriptions.map((description, index) => (
        <BodyText variant="medium" as="p" key={index}>
          {description}
        </BodyText>
      ))}
      <HelpPane />
    </Modal>
  )
}

function getNonRefundableDescription(intl: IntlShape): string {
  return intl.formatMessage({
    id: 'APjp0vPEwaRrlUscFjCF',
    defaultMessage: 'Please note that ’Non-Refundable’ packages are guaranteed at the time of booking and not eligible for a refund or date change. Accordingly, please select your package carefully and take note of any cancellation policy applicable to your purchase.',
    description: 'Detailed explanation of non-refundable policy in modal',
  })
}

function getGenericCancellationFallbackDescription(
  cancellationPolicy: unknown,
  intl: IntlShape,
): string {
  if (isNonRefundableCancellationPolicy(cancellationPolicy)) {
    return getNonRefundableDescription(intl)
  }

  if (isRecord(cancellationPolicy) && cancellationPolicy.partiallyRefundable === true) {
    return intl.formatMessage({
      id: 'Vn3mq_kFKwRgjjX8_E0N',
      defaultMessage: 'This rate may be partially refundable. Check the cancellation policy before booking.',
      description: 'Fallback explanation for partially refundable hotel room option cancellation modal',
    })
  }

  return intl.formatMessage({
    id: 's0W2mmdPtqX5njnMYdpJ',
    defaultMessage: 'Cancellation terms are set by the supplier and may vary by rate. Check the cancellation policy before booking.',
    description: 'Fallback explanation for hotel room option cancellation modal',
  })
}

function getBedbankCancellationPolicyHeading(
  cancellationPolicy: unknown,
  fallbackTitle: string,
  intl: IntlShape,
): string {
  if (!isRecord(cancellationPolicy)) return fallbackTitle

  if (cancellationPolicy.partiallyRefundable === true) {
    return intl.formatMessage({
      id: '5MIsi9WIE3If_SkiXbxP',
      defaultMessage: 'Partially refundable',
      description: 'Modal heading for partially refundable bedbank cancellation policy',
    })
  }

  if (cancellationPolicy.refundable === true) {
    return intl.formatMessage({
      id: '2bNqBX95lob5QLgpI0E7',
      defaultMessage: 'Free cancellation',
      description: 'Modal heading for free cancellation bedbank cancellation policy',
    })
  }

  return fallbackTitle
}

export function showCancellationPolicyModal({
  cancellationPolicy,
  title,
  checkIn,
  purchaseDate,
  intl,
  showModal,
}: {
  cancellationPolicy: unknown
  title: string
  checkIn?: string
  purchaseDate?: string
  intl: IntlShape
  showModal: (content: ReactNode) => void
}) {
  if (isBedbankCancellationPolicy(cancellationPolicy)) {
    showModal(
      <BedbankCancellationPolicyModal
        heading={getBedbankCancellationPolicyHeading(cancellationPolicy, title, intl)}
        policyDescriptions={cancellationPolicy.description}
      />,
    )
    return
  }

  if (isOfferCancellationPolicy(cancellationPolicy)) {
    if (
      cancellationPolicy.type === 'post-purchase-seven-days' &&
      isPostPurchaseSevenDayCancellationActive(checkIn, purchaseDate)
    ) {
      showModal(
        <OfferPropertyCancellationPolicyModal
          startDate={getFreeCancellationStartDate(checkIn, purchaseDate)}
          checkInDate={checkIn}
        />,
      )
      return
    }

    if (isFlexibleOfferCancellationPolicy(cancellationPolicy)) {
      const timelineDetails = getFlexibleCancellationTimelineDetails({
        cancellationPolicy,
        checkInDate: checkIn,
        purchaseDate,
      })

      showModal(
        <OfferPropertyFlexibleCancellationPolicyTimelineModal
          details={timelineDetails}
        />,
      )
      return
    }
  }

  const descriptions = isRecord(cancellationPolicy)
    ? [
        ...getStringArray(cancellationPolicy.policyDetail),
        ...getStringArray(cancellationPolicy.description),
      ]
    : []

  showModal(
    <GenericCancellationPolicyModal
      title={title}
      descriptions={descriptions.length > 0
        ? descriptions
        : [getGenericCancellationFallbackDescription(cancellationPolicy, intl)]}
    />,
  )
}
